import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {DetailField} from '@/components/shared/DetailField'
import {TextLink} from '@/components/shared/TextLink'
import {Stack} from '@/components/shared/Stack'
import {SmallText} from '@/components/shared/SmallText'
import {formatTimestamp} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {useI18n} from '@/i18n'
import type {RestrictionRuleRequest} from '@/types/restriction-rule-request'

interface ReviewInfoCardProps {
    request: RestrictionRuleRequest
}

/**
 * Sidebar card that surfaces the reviewer's decision metadata for a restriction
 * rule request. The card is rendered only when a decision has been recorded —
 * the caller must check {@link shouldShowReviewInfoCard} before mounting it
 * (UX spec §6.1: hide the card entirely while DRAFT/PENDING/CANCELLED rather
 * than show empty dashes).
 */
export function ReviewInfoCard({request}: ReviewInfoCardProps) {
    const i18n = useI18n()

    const reviewedBy = request.reviewedBy
    const reviewerLink = reviewedBy ? (
        <TextLink
            testId="link-reviewed-by"
            to={routes.identityManagement.identities.detail(reviewedBy.id)}
            variant="bold"
        >
            {reviewedBy.displayName ?? reviewedBy.id}
        </TextLink>
    ) : null

    return (
        <Card data-testid="review-info-card" data-help-prefix="reviewInfo">
            <CardHeader>
                <CardTitle data-help-key="_section" data-testid="card-title-review-info">
                    {i18n.restrictionRuleRequests.reviewInfoTitle}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Stack gap="md">
                    <dl>
                        {reviewedBy && (
                            <div data-testid="review-info-reviewer">
                                <DetailField
                                    label={i18n.restrictionRuleRequests.reviewedBy}
                                    value={reviewerLink}
                                />
                            </div>
                        )}
                        {request.reviewedAt && (
                            <div data-testid="review-info-reviewed-at">
                                <DetailField
                                    label={i18n.restrictionRuleRequests.reviewedAt}
                                    value={formatTimestamp(request.reviewedAt)}
                                />
                            </div>
                        )}
                        {request.reviewComment && (
                            <div data-testid="review-info-comment">
                                <DetailField
                                    label={i18n.restrictionRuleRequests.reviewComment}
                                    value={<SmallText>{request.reviewComment}</SmallText>}
                                    fullWidth
                                />
                            </div>
                        )}
                    </dl>
                </Stack>
            </CardContent>
        </Card>
    )
}

/**
 * Returns true when the request has a recorded reviewer decision (any of
 * reviewer identity, reviewedAt, or comment is present). The component
 * hides itself entirely otherwise — see UX spec §6.1.
 */
export function shouldShowReviewInfoCard(request: RestrictionRuleRequest): boolean {
    return Boolean(
        request.reviewedAt ||
        request.reviewedBy ||
        request.reviewComment,
    )
}
